import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, Image, Svg, Path, Circle } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

// Define A4 dimensions and styles
const styles = StyleSheet.create({
    page: {
        paddingTop: 24,
        paddingHorizontal: 34,
        paddingBottom: 34,
        fontSize: 10,
        fontFamily: 'Helvetica',
        color: '#303030',
    },
    section: {
        marginBottom: 10,
        padding: 10,
        borderBottom: '0.5 solid black',
        borderRadius: 0,
    },
    logo: {
        width: 80,
        height: 50,
        marginRight: 15, // Space between the logo and the text
        objectFit: 'contain',
    },
    headerContainer: {
        flex: 1, // Take up the remaining space after the logo
    },
    header: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 10,
    },
    subHeader: {
        fontSize: 10,
        textAlign: 'center',
        color: 'gray',
    },
    row: {
        flexDirection: 'row',
        marginVertical: 4,
    },
    label: {
        fontSize: 10,
        fontWeight: 'bold',
    },
    value: {
        fontSize: 10,
        marginLeft: 6,
    },
    divider: {
        marginVertical: 5,
        borderBottomWidth: 1,
        borderBottomColor: 'black',
    },
    letterheadSection: {
        marginBottom: 13,
        paddingBottom: 10,
        borderBottomWidth: 1.5,
        borderBottomColor: '#858552',
        flexDirection: 'row', // Align items horizontally
        alignItems: 'center', // Vertically center content
    },
    headerLogo: {
        width: 62,
        height: 62,
        objectFit: 'contain',
        marginRight: 14,
    },
    brandBlock: {
        flex: 1,
    },
    brandName: {
        fontSize: 13,
        fontWeight: 'bold',
        letterSpacing: 1.2,
        color: '#383838',
        marginBottom: 3,
    },
    brandLabel: {
        fontSize: 7,
        letterSpacing: 2,
        color: '#777777',
        marginBottom: 7,
    },
    serviceLine: {
        fontSize: 7,
        lineHeight: 1.4,
        color: '#555555',
    },
    companyContact: {
        marginTop: 3,
        fontSize: 7,
        lineHeight: 1.4,
        color: '#666666',
    },
    signatureBlock: {
        marginTop: 12,
        marginBottom: 10,
        padding: 9,
        borderLeftWidth: 2,
        borderLeftColor: '#858552',
        backgroundColor: '#f8f8f6',
    },
    signatureThanks: {
        fontSize: 9,
        color: '#555555',
        marginBottom: 7,
    },
    signatureName: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#303030',
    },
    signatureCompany: {
        fontSize: 8,
        marginTop: 2,
        color: '#777777',
    },
    quotationBanner: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 11,
        paddingBottom: 7,
        borderBottomWidth: 0.7,
        borderBottomColor: '#d7d7d0',
    },
    quotationTitle: {
        fontSize: 17,
        fontWeight: 'bold',
        color: '#383838',
        letterSpacing: 1,
    },
    customerCard: {
        flexDirection: 'row',
        marginBottom: 8,
        paddingBottom: 9,
        borderBottomWidth: 0.7,
        borderBottomColor: '#d7d7d0',
    },
    metaRow: {
        flex: 1,
        paddingLeft: 14,
        borderLeftWidth: 1,
        borderLeftColor: '#d7d7d0',
        justifyContent: 'center',
    },
    customerRow: {
        flexDirection: 'row',
        marginTop: 4,
        alignItems: 'flex-start',
    },
    customerLabel: {
        width: 78,
        fontSize: 9,
        fontWeight: 'bold',
        color: '#555555',
    },
    customerValue: {
        fontSize: 9,
        lineHeight: 1.35,
        color: '#303030',
    },
    metaValue: {
        fontSize: 8.5,
        marginBottom: 5,
        color: '#303030',
    },
    customerInfo: {
        flex: 1.4,
    },
    sectionHeading: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#383838',
        marginTop: 9,
        marginBottom: 5,
    },
    summaryLabel: {
        flex: 5,
        textAlign: 'right',
        paddingVertical: 4,
        paddingRight: 10,
        fontSize: 8.5,
        color: '#555555',
    },
    summaryValue: {
        flex: 2,
        textAlign: 'right',
        paddingVertical: 4,
        paddingRight: 7,
        fontSize: 8.5,
        color: '#303030',
    },
    grandTotalLabel: {
        flex: 5,
        textAlign: 'right',
        paddingVertical: 7,
        paddingRight: 10,
        fontSize: 10,
        fontWeight: 'bold',
        color: '#ffffff',
        backgroundColor: '#858552',
    },
    grandTotalValue: {
        flex: 2,
        textAlign: 'right',
        paddingVertical: 7,
        paddingRight: 7,
        fontSize: 10,
        fontWeight: 'bold',
        color: '#ffffff',
        backgroundColor: '#858552',
    },
    amountWords: {
        paddingVertical: 6,
        paddingHorizontal: 8,
        fontSize: 8,
        lineHeight: 1.4,
        color: '#555555',
        backgroundColor: '#f6f6f3',
    },
    pageFooter: {
        position: 'absolute',
        bottom: 14,
        left: 34,
        right: 34,
        paddingTop: 5,
        borderTopWidth: 0.5,
        borderTopColor: '#d7d7d0',
        flexDirection: 'row',
        justifyContent: 'space-between',
        color: '#777777',
        fontSize: 7,
    },
    table: {
        display: 'table',
        width: 'auto',
        borderWidth: 1,
        borderColor: 'black',
        marginVertical: 10,
    },
    tableRow: {
        flexDirection: 'row',
    },
    tableHeader: {
        backgroundColor: '#383838',
        borderWidth: 0,
        borderColor: '#383838',
        color: '#ffffff',
        textAlign: 'center',
        paddingVertical: 7,
        paddingHorizontal: 4,
        fontSize: 8,
        fontWeight: 'bold',
    },
    tableCell: {
        borderColor: '#deded9',
        paddingVertical: 5,
        paddingHorizontal: 4,
        textAlign: 'center',
        borderBottomWidth: 0.5,
        borderLeftWidth: 0,
        fontSize: 8,
        fontFamily: 'Helvetica'
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 5,
    },
    chartContainer: {
        marginTop: 20,
        marginBottom: 20,
    },
    chartBar: {
        height: 20,
        marginBottom: 6,
        borderRadius: 5,
    },
    chartLabel: {
        fontSize: 10,
        marginBottom: 4,
        fontWeight: 'bold',
        width: '20%'
    },
    chartBarContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    footer: {
        marginVertical: 20,
        textAlign: "left",
        fontSize: 10,
    },
    contactCard: {
        marginTop: 14,
        paddingVertical: 12,
        paddingHorizontal: 14,
        backgroundColor: "#171717",
        borderRadius: 4,
    },
    contactHeading: {
        color: "#FFFFFF",
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 10,
    },
    contactAddressRow: {
        flexDirection: "row",
        alignItems: "flex-start",
    },
    contactPin: {
        width: 12,
        height: 16,
        marginTop: 1,
        marginRight: 8,
    },
    contactAddress: {
        flex: 1,
        color: "#A8C4D6",
        fontSize: 9,
        lineHeight: 1.6,
    },
    contactDetails: {
        flexDirection: "row",
        marginTop: 10,
        paddingTop: 8,
        borderTopWidth: 0.5,
        borderTopColor: "#454545",
    },
    contactDetail: {
        width: "33.33%",
        paddingRight: 6,
    },
    contactLabel: {
        color: "#A8C4D6",
        fontSize: 7,
        marginBottom: 3,
    },
    contactValue: {
        color: "#FFFFFF",
        fontSize: 8,
    },
    boldText: {
        fontWeight: "bold",
    },
    blueText: {
        color: "#1E90FF",
        fontSize: 9,
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: "bold",
        marginBottom: 10,
        textDecoration: "underline",
    },
    terms: {
        fontSize: 10,
        lineHeight: 1.5,
        marginBottom: 5,
    },
    bold: {
        fontWeight: 'bold',
    },
    subSection: {
        fontSize: 10,
        fontWeight: 'bold',
        marginTop: 5,
        marginBottom: 3,
    },
    listItem: {
        fontWeight: 'bold',
    },
});

// Quotation PDF Component
const QuotationPDF = ({ data, totalAmountInWords, maxDays }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            {/* Header Section */}
            <View style={styles.letterheadSection}>
                <Image style={styles.headerLogo} src={require('../../../assets/images/fina-interior-logo.jpg')} />
                <View style={styles.brandBlock}>
                    <Text style={styles.brandName}>INTERIOR DESIGN &amp; EXECUTION</Text>
                    <Text style={styles.serviceLine}>
                        Renovation | Customized Furniture | Flooring | Modern Kitchen | Lighting &amp; False Ceiling | Soft Furnishing
                    </Text>
                    <Text style={styles.companyContact}>
                        Shop No 1, Apartment, Mohammed Wadi Rd, near Desai Hospital, opp. Margosa Heights, Autadwadi Handewadi, Mahadev Wadi, Hadapsar, Pune, Maharashtra 411060
                    </Text>
                    <Text style={styles.companyContact}>
                        +91 9112881916  |  Finainterior29@gmail.com  |  www.finainterior.com
                    </Text>
                </View>
            </View>

            <View style={styles.quotationBanner}>
                <Text style={styles.quotationTitle}>QUOTATION</Text>
                <Text style={styles.metaValue}>Quotation No:  #{data?.id}</Text>
            </View>

            {/* Customer and quotation information */}
            <View style={styles.customerCard}>
                <View style={styles.customerInfo}>
                    <Text style={[styles.customerLabel, { width: 'auto', marginBottom: 4 }]}>PREPARED FOR</Text>
                    <Text style={[styles.customerValue, { fontSize: 11, fontWeight: 'bold' }]}>{data?.customer_name}</Text>
                    <Text style={[styles.customerValue, { marginTop: 4 }]}>{data?.address}</Text>
                    <Text style={[styles.customerValue, { marginTop: 3 }]}>Mobile: {data?.phone}</Text>
                </View>
                <View style={styles.metaRow}>
                    <Text style={styles.metaValue}>Date:  {data?.created_at?.slice(0, 10)}</Text>
                    <Text style={styles.metaValue}>Prepared by:  {data?.created_by_name || 'Fina Interior'}</Text>
                </View>
            </View>
            <Text style={styles.sectionHeading}>Quotation for Interior Services</Text>
            <View style={styles.table}>
                {/* Table Header */}
                <View style={styles.tableRow}>
                    <Text style={[styles.tableHeader, { flex: 1 }]}>Sr.No.</Text>
                    <Text style={[styles.tableHeader, { flex: 3 }]}>Description</Text>
                    <Text style={[styles.tableHeader, { flex: 1 }]}>Size</Text>
                    <Text style={[styles.tableHeader, { flex: 1 }]}>Quantity</Text>
                    <Text style={[styles.tableHeader, { flex: 1 }]}>Rate</Text>
                    <Text style={[styles.tableHeader, { flex: 1 }]}>Amount</Text>
                </View>

                {/* Table Body */}
                {data?.items?.map((item, index) => (
                    <View key={item.id}>
                        {/* Item Title Row */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { flex: 8, fontWeight: 'bold', textAlign: 'left', backgroundColor: '#f3f2ec' }]}>
                                {item.title}
                            </Text>
                        </View>

                        {/* Subfields */}
                        {item?.subfiled?.map((subfield, subIndex) => (
                            <View key={subIndex} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{index + 1}.{subIndex + 1}</Text>
                                <Text style={[styles.tableCell, { flex: 3 }]}>{subfield.description}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{subfield.size}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{subfield.quantity}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{subfield.rate}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{subfield.amount}</Text>
                            </View>
                        ))}

                    </View>
                ))}
                <View style={styles.tableRow}>
                    <Text style={styles.summaryLabel}>Sub Total</Text>
                    <Text style={styles.summaryValue}>₹ {data?.total}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.summaryLabel}>Discount ({data?.discount}%)</Text>
                    <Text style={styles.summaryValue}>₹ {data?.discount_amount}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.summaryLabel}>SGST</Text>
                    <Text style={styles.summaryValue}>₹ {data?.sgst}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.summaryLabel}>CGST</Text>
                    <Text style={styles.summaryValue}>₹ {data?.cgst}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.grandTotalLabel}>GRAND TOTAL</Text>
                    <Text style={styles.grandTotalValue}>₹ {data?.grand_total}</Text>
                </View>
                {/* Amount in Words Row */}
                <View style={styles.tableRow}>
                    <Text style={styles.amountWords}>
                        Amount in words: {totalAmountInWords} rupees only
                    </Text>
                </View>
            </View>

            {/* Payment Slab Section */}
            <Text style={styles.sectionHeading}>Payment Schedule</Text>
            <View style={styles.table}>
                <View style={styles.tableRow}>
                    <Text style={[styles.tableCell, { flex: 3, fontWeight: 'bold' }]}>Payment Slab</Text>
                    <Text style={[styles.tableCell, { flex: 2, fontWeight: 'bold' }]}>Percentage</Text>
                    <Text style={[styles.tableCell, { flex: 2, fontWeight: 'bold' }]}>Amount</Text>
                    <Text style={[styles.tableCell, { flex: 2, fontWeight: 'bold' }]}>Due Date</Text>
                </View>

                {data?.installments?.map((item, index) => (
                    <View key={index} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { flex: 3 }]}>
                            {item.label}
                        </Text>
                        <Text style={[styles.tableCell, { flex: 2 }]}>
                            {item.percentage}%
                        </Text>
                        <Text style={[styles.tableCell, { flex: 2 }]}>
                            {parseFloat(item.amount).toFixed(2)}
                        </Text>
                        <Text style={[styles.tableCell, { flex: 2 }]}>
                            {item.due_date}
                        </Text>
                    </View>
                ))}
            </View>
            <Text style={styles.sectionHeading}>Material Specifications</Text>
            <View style={styles.table}>

                {/* Table Header */}
                {data?.mark_list?.map((category, index) => (
                    <View key={index}>
                        {/* Category Row */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { flex: 5, fontWeight: 'bold', fontSize: 11 }]}>
                                {category.title}
                            </Text>
                            <Text style={[styles.tableCell, { flex: 2 }]}>
                                Mark
                            </Text>
                        </View>

                        {/* Mapping through the items in the category */}
                        {category?.children?.map((item, idx) => (
                            <View key={idx} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { flex: 5, fontWeight: 'light', fontSize: 9 }]}>
                                    {item.title}
                                </Text>
                                <Text style={[styles.tableCell, { flex: 2, textAlign: 'center' }]}>
                                    {/* Mark column */}
                                    <Text style={{ color: 'green' }}>✔</Text> {/* Tick mark */}
                                </Text>
                            </View>
                        ))}
                    </View>
                ))}
            </View>
            <Text style={styles.sectionHeading}>Project Timeline</Text>
            <View style={styles.chartContainer}>
                {data?.timelines?.map((timeline) => {
                    const task = timeline.task;
                    const days = parseInt(timeline.days);
                    const barWidth = (days / maxDays) * 300;  // Normalize bar length based on max days

                    return (
                        <View key={timeline.id} style={styles.chartBarContainer}>
                            <Text style={styles.chartLabel}>{task}</Text>
                            <View style={[styles.chartBar, { width: barWidth, backgroundColor: '#4BC0C0' }]} />
                            <Text style={styles.chartLabel}>{days} days</Text>
                        </View>
                    );
                })}
                <Text style={[styles.header, { fontSize: 14, marginVertical: 10 }]}>
                    Total Days - {data?.timelines?.reduce((acc, curr) => acc + parseInt(curr.days), 0)}
                </Text>
            </View>
            {/* Terms and Conditions Section */}
            <Text style={styles.sectionHeading}>Terms and Conditions</Text>
            <View>
                <Text style={styles.terms}>
                    1. Scope of the quotation is limited to above mentioned areas.
                </Text>
                <Text style={styles.terms}>
                    2. Any changes in the approved design will attract additional cost, quotation, and longer delivery period.
                </Text>
                <Text style={styles.terms}>
                    3. Bathroom, terrace, passage, study room, balcony, open plot, dry balcony, and wash basin are treated as different entities.
                </Text>
                <Text style={styles.terms}>
                    4. Design, painting, furnishing, electrical fit-out/work, and other fittings for the above entities are not included in the quotation unless requested specifically.
                </Text>
                <Text style={styles.terms}>
                    5. Any delay in the selection process of electronic/electrical/furnishing/other fittings will proportionately delay the delivery timelines.
                </Text>
                <Text style={styles.terms}>
                    6. Warranty: Electrical fittings – 2 years; electronic/electrical gadgets (as per manufacturer guarantee policy).
                </Text>
                <Text style={styles.terms}>7. Warranty: Furniture hardware – 10 years.</Text>
            </View>

            <Text style={styles.sectionHeading}>Payment Terms</Text>
            <View>
                <Text style={styles.terms}>
                    Payment terms are structured to align with key project milestones, ensuring transparency and seamless execution. Clients are requested to adhere to the agreed payment schedule to maintain project timelines. In case of any delays, we encourage open communication to find a suitable resolution and minimize disruptions.                </Text>
                <Text style={styles.subSection}>Payment Schedule:</Text>
                <Text style={styles.terms}>
                    • <Text style={styles.listItem}>20% Initial Payment:</Text> To confirm the project and commence the design phase. Covers design, false ceiling work, and electrical work completion.
                </Text>
                <Text style={styles.terms}>
                    • <Text style={styles.listItem}>35% Furniture Payment:</Text> Due before the start of furniture manufacturing and installation.
                </Text>
                <Text style={styles.terms}>
                    • <Text style={styles.listItem}>35% Final Procurement Payment:</Text> Due prior to the procurement of hardware and soft furnishing materials.
                </Text>

                <Text style={styles.subSection}>Modes of Payment:</Text>
                <Text style={styles.terms}>
                    • Online bank transfers (details provided in the invoice).
                </Text>
                <Text style={styles.terms}>
                    • Cheque payments (subject to clearance).
                </Text>
                <Text style={styles.terms}>
                    • Accepted digital payment platforms (as specified by the company).
                </Text>

                <Text style={styles.subSection}>Taxes and Additional Charges:</Text>
                <Text style={styles.terms}>
                    • All payments are subject to GST and applicable taxes as per government regulations.
                </Text>
                <Text style={styles.terms}>
                    • Any additional requests or changes by the client beyond the original scope of work will incur separate charges.
                </Text>
            </View>
            <View style={styles.signatureBlock} wrap={false}>
                <Text style={styles.signatureThanks}>We look forward to working with you.</Text>
                <Text style={styles.signatureName}>Tausif Khan</Text>
                <Text style={styles.signatureCompany}>Fina Interior</Text>
            </View>
            <View style={styles.contactCard} wrap={false}>
                <Text style={styles.contactHeading}>VISIT OR CONTACT</Text>
                <View style={styles.contactAddressRow}>
                    <Svg style={styles.contactPin} viewBox="0 0 12 16">
                        <Path d="M6 15s5-5.4 5-9A5 5 0 1 0 1 6c0 3.6 5 9 5 9Z" fill="#A8C4D6" />
                        <Circle cx="6" cy="6" r="1.8" fill="#171717" />
                    </Svg>
                    <Text style={styles.contactAddress}>
                        Shop No 1, Apartment, Mohammed Wadi Rd, near Desai Hospital, opp. Margosa Heights, Autadwadi Handewadi, Mahadev Wadi, Hadapsar, Pune, Maharashtra 411060
                    </Text>
                </View>
                <View style={styles.contactDetails}>
                    <View style={styles.contactDetail}>
                        <Text style={styles.contactLabel}>PHONE</Text>
                        <Text style={styles.contactValue}>+91 9112881916</Text>
                    </View>
                    <View style={styles.contactDetail}>
                        <Text style={styles.contactLabel}>EMAIL</Text>
                        <Text style={styles.contactValue}>Finainterior29@gmail.com</Text>
                    </View>
                    <View style={styles.contactDetail}>
                        <Text style={styles.contactLabel}>WEBSITE</Text>
                        <Text style={styles.contactValue}>www.finainterior.com</Text>
                    </View>
                </View>
            </View>
            <View style={styles.pageFooter} fixed>
                <Text>FINA INTERIOR  |  INTERIOR DESIGN &amp; EXECUTION</Text>
                <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </View>
        </Page>
    </Document >
);

// Prop Types for Data Validation
QuotationPDF.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.string.isRequired,
        created_at: PropTypes.string.isRequired,
        customer_name: PropTypes.string.isRequired,
        address: PropTypes.string.isRequired,
        phone: PropTypes.string.isRequired,
        total: PropTypes.number.isRequired,
        discount: PropTypes.number.isRequired,
        discount_amount: PropTypes.number.isRequired,
        sgst: PropTypes.number.isRequired,
        cgst: PropTypes.number.isRequired,
        grand_total: PropTypes.number.isRequired,
        created_by_name: PropTypes.string.isRequired,
        mobile_no: PropTypes.string.isRequired,
        installments: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                percentage: PropTypes.number.isRequired,
                amount: PropTypes.string.isRequired,
                due_date: PropTypes.string.isRequired,
            })
        ).isRequired,
        mark_list: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string.isRequired,
                children: PropTypes.arrayOf(
                    PropTypes.shape({
                        title: PropTypes.string.isRequired,
                    })
                ).isRequired,
            })
        ).isRequired,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string.isRequired,
                subfiled: PropTypes.arrayOf(
                    PropTypes.shape({
                        description: PropTypes.string.isRequired,
                        size: PropTypes.string.isRequired,
                        quantity: PropTypes.number.isRequired,
                        rate: PropTypes.number.isRequired,
                        amount: PropTypes.number.isRequired,
                    })
                ).isRequired,
            })
        ).isRequired,
        timelines: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                quotation_id: PropTypes.string.isRequired,
                task: PropTypes.string.isRequired,
                days: PropTypes.string.isRequired,
            })
        ).isRequired,
    }).isRequired,
    totalAmountInWords: PropTypes.string.isRequired,
    maxDays: PropTypes.string.isRequired,
};

export default QuotationPDF;
